// ─── inline link templates ──────────────────────────────────────────────────
//
// A catalog sentence can mark the spans that are links with named tags:
//
//   I agree to the <terms>Terms of Use</terms> and <privacy>Privacy Policy</privacy>.
//
// The sentence stays one translatable string (a translation may reorder the
// links) and the tag names are the stable seam a screen binds its link
// targets to. This module turns such a string into ordered runs; it knows
// nothing about which document a tag opens. Tags are the only markup: a
// translation that mistypes one degrades to plain text, never to raw markup.

/** A span of a tagged sentence: plain text, or the text of one named link. */
export type InlineRun =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'link'; readonly tag: string; readonly text: string };

const TAG = /<([A-Za-z][A-Za-z0-9]*)>([^<]*)<\/\1>/g;

/**
 * Splits a tagged sentence into ordered runs. A well-formed `<tag>text</tag>`
 * becomes a link run; everything else, including an unclosed tag, a mismatched
 * closing tag or a tag whose text holds another tag, stays in a text run as
 * written (less any tag markup `stripInlineLinkTags` would remove). Adjacent
 * text is merged into one run, and empty runs are dropped.
 */
export function splitInlineLinkTemplate(template: string): InlineRun[] {
  const runs: InlineRun[] = [];
  const pushText = (text: string) => {
    if (text.length === 0) return;
    const last = runs[runs.length - 1];
    if (last && last.kind === 'text') {
      runs[runs.length - 1] = { kind: 'text', text: last.text + text };
    } else {
      runs.push({ kind: 'text', text });
    }
  };
  let cursor = 0;
  for (const match of template.matchAll(TAG)) {
    const index = match.index ?? 0;
    pushText(stripInlineLinkTags(template.slice(cursor, index)));
    const [, tag = '', text = ''] = match;
    if (text.length > 0) runs.push({ kind: 'link', tag, text });
    cursor = index + match[0].length;
  }
  pushText(stripInlineLinkTags(template.slice(cursor)));
  return runs;
}

/** The sentence with every tag removed: the accessible name of the control it labels. */
export function stripInlineLinkTags(template: string): string {
  return template.replace(/<\/?[A-Za-z][A-Za-z0-9]*>/g, '');
}
